import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CreateEnvironmentInput,
  Environment,
  UpdateEnvironmentInput,
} from './environments-types';
import { EnvironmentsForm } from './environments-form';
import {
  useCreateEnvironment,
  useDeleteEnvironment,
  useEnvironments,
  useUpdateEnvironment,
} from './environments-queries';

export function EnvironmentsPage(): JSX.Element {
  const { data: envs, isLoading, error } = useEnvironments();
  const createMut = useCreateEnvironment();
  const updateMut = useUpdateEnvironment();
  const deleteMut = useDeleteEnvironment();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Environment | null>(null);
  const [deleting, setDeleting] = useState<Environment | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Environments</h2>
          <p className="text-sm text-muted-foreground">
            Labels used to classify base URLs in the catalog (production / staging / development).
            Three are seeded by default. Admin-only — never affects the public API.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Environment
        </Button>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">Failed to load environments.</p>}

      {envs && envs.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">No environments yet.</p>
        </div>
      )}

      {envs && envs.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-24">Sort</TableHead>
              <TableHead className="w-24">Active</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {envs.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-sm">{e.code}</TableCell>
                <TableCell>{e.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{e.sort_order}</TableCell>
                <TableCell>{e.is_active ? '✓' : '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(e)} aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(e)} aria-label="Delete">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Environment</DialogTitle>
          </DialogHeader>
          <EnvironmentsForm
            onSubmit={async (data) => {
              await createMut.mutateAsync(data as CreateEnvironmentInput);
              setCreateOpen(false);
            }}
            onCancel={() => setCreateOpen(false)}
            isSubmitting={createMut.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Environment</DialogTitle>
          </DialogHeader>
          {editing && (
            <EnvironmentsForm
              initial={editing}
              onSubmit={async (data) => {
                await updateMut.mutateAsync({ id: editing.id, input: data as UpdateEnvironmentInput });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              isSubmitting={updateMut.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete environment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will fail if any <code>game_base_urls</code> row still references{' '}
              <code>{deleting?.code}</code>. Remove catalog entries first, then delete this env.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleting) {
                  await deleteMut.mutateAsync(deleting.id);
                  setDeleting(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
